import {
  Channel,
  ChannelMetricsData,
  DashboardStats,
  LogEntry,
  Settings,
  Task,
  TruncateLogsResult,
  TruncateMetricsResult,
  TrustRegistry,
  UserSettingsOverrides,
} from './types';

const API_BASE = '/api/v1';

class SessionManager {
  private setCookie(name: string, value: string, days: number = 365): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
  }

  getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  getSessionToken(): string | null {
    // Try sessionStorage first
    let token = sessionStorage.getItem('session_token');
    if (token) return token;

    token = this.getCookie('session_token');
    if (token) {
      // Restore to sessionStorage for future calls
      sessionStorage.setItem('session_token', token);
    }
    return token;
  }

  getAuthHeaders(): Record<string, string> {
    const sessionToken = this.getSessionToken();
    if (!sessionToken) {
      return {};
    }
    return { Authorization: `Bearer ${sessionToken}` };
  }

  login(username: string, sessionToken: string): void {
    this.setCookie('lastUsername', username);
    sessionStorage.setItem('session_token', sessionToken);
  }

  async logout(logoutAction: () => void = () => {}): Promise<void> {
    const token = this.getSessionToken();
    logoutAction();
    if (token) {
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: this.getAuthHeaders(),
        });
      } catch (error) {
        console.error('Logout failed:', error);
      }
    }

    sessionStorage.removeItem('session_token');
    document.cookie = 'session_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  }
}

export const sessionManager = new SessionManager();

class APIClient {
  private async request<T>(
    endpoint: string,
    options?: RequestInit & { timeout?: number }
  ): Promise<T> {
    const url = `${API_BASE}${endpoint}`;

    // Setup timeout using AbortController
    const controller = new AbortController();
    const timeoutId = options?.timeout
      ? setTimeout(() => controller.abort(), options.timeout)
      : null;

    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...sessionManager.getAuthHeaders(),
          ...options?.headers,
        },
        ...options,
        signal: controller.signal,
      });

      if (!response.ok) {
        let errorMessage = `API Error: ${response.status} ${response.statusText}`;
        console.error('[API] Request failed:', endpoint, response.status, response.statusText);
        try {
          const errorBody = await response.text();
          if (errorBody) {
            errorMessage += ` - ${errorBody}`;
            console.error('[API] Error body:', errorBody.substring(0, 500));
          }
        } catch (e) {
          console.error('[API] Could not read error response:', e);
        }
        throw new Error(errorMessage);
      }

      // Don't try to parse JSON for 204 No Content responses
      if (response.status === 204) {
        return {} as T;
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - the webhook endpoint did not respond within 30 seconds');
      }
      throw error;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  /**
   * Authenticated fetch — drop-in replacement for `fetch()` that adds the
   * Authorization header automatically.  Returns the raw `Response` so callers
   * can inspect status, stream the body, etc.
   */
  async fetch(input: string, init?: RequestInit): Promise<Response> {
    return fetch(input, {
      ...init,
      headers: {
        ...sessionManager.getAuthHeaders(),
        ...init?.headers,
      },
    });
  }

  // Generic HTTP methods
  async get<T = any>(endpoint: string, options?: { timeout?: number }): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, options);
    return { data };
  }

  async post<T = any>(
    endpoint: string,
    body?: any,
    options?: { timeout?: number }
  ): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
      ...options,
    });
    return { data };
  }

  async put<T = any>(
    endpoint: string,
    body?: any,
    options?: { timeout?: number }
  ): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
      ...options,
    });
    return { data };
  }

  async delete<T = any>(endpoint: string, options?: { timeout?: number }): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, {
      method: 'DELETE',
      ...options,
    });
    return { data };
  }

  // Dashboard Stats
  async getDashboardStats(
    bucketSeconds?: number,
    channelId?: string,
    gatewayId?: string,
    identityDid?: string,
    pipeId?: string
  ): Promise<DashboardStats> {
    const params = new URLSearchParams();
    if (bucketSeconds) params.append('bucket_seconds', bucketSeconds.toString());
    if (channelId) params.append('channel_id', channelId);
    if (gatewayId) params.append('gateway_id', gatewayId);
    if (identityDid) params.append('identity_did', identityDid);
    if (pipeId) params.append('pipe_id', pipeId);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const result = await this.request<DashboardStats>(`/dashboard/stats${queryString}`);

    return result;
  }

  async getChannelMetrics(channelConfigId: string): Promise<ChannelMetricsData> {
    return this.request<ChannelMetricsData>(
      `/dashboard/channel/${encodeURIComponent(channelConfigId)}/metrics`
    );
  }

  async getHierarchicalMetrics(): Promise<any> {
    return this.request('/dashboard/metrics/hierarchical');
  }

  async getSystemMetrics(timeRange?: string, since?: string): Promise<any> {
    const params = new URLSearchParams();
    if (since) params.set('since', since);
    else if (timeRange) params.set('time_range', timeRange);
    const qs = params.toString();
    return this.request(`/dashboard/system-metrics${qs ? `?${qs}` : ''}`);
  }

  // Identity Management
  async resolveIdentityDID(did: string): Promise<any> {
    return this.request(`/identity/resolve-did?did=${encodeURIComponent(did)}`);
  }

  async getGatewayDidDocument(): Promise<any> {
    return this.request('/identity/did-document');
  }

  async retryTrRegistration(identityHash: string): Promise<any> {
    return this.post(`/identities/${encodeURIComponent(identityHash)}/register-trust-registry`);
  }

  async retryDeptTrRegistration(deptId: string): Promise<any> {
    return this.post(`/departments/${encodeURIComponent(deptId)}/register-trust-registry`);
  }

  // Settings Management
  async getSettings(): Promise<Settings> {
    const backendSettings = await this.request<any>('/settings');

    // Convert backend settings format to frontend format
    return {
      badge_threshold: backendSettings.badge_threshold_minutes || 5,
      metrics_retention: backendSettings.metrics_retention_minutes || 360, // Both in minutes now
      task_activity_window: backendSettings.task_activity_window_seconds || 60,
      connections_window: backendSettings.connections_window_minutes || 5,
      latency_window: backendSettings.latency_window_minutes || 5,
      onboarding_channel_ttl_seconds: backendSettings.onboarding_channel_ttl_seconds || 30,
      refresh_interval_seconds: backendSettings.refresh_interval_seconds || 5,
      log_timestamp_format: backendSettings.log_timestamp_format || 'local',
      bucket_seconds: backendSettings.bucket_seconds || 30,
      payments_min_display: backendSettings.payments_min_display || 10,
      feature_flags: backendSettings.feature_flags || {},
    };
  }

  async updateSettings(settings: Partial<Settings>): Promise<void> {
    // Map frontend field names to backend field names
    const backendSettings: any = {};

    if (settings.badge_threshold !== undefined) {
      backendSettings.badge_threshold_minutes = settings.badge_threshold;
    }

    if (settings.metrics_retention !== undefined) {
      backendSettings.metrics_retention_minutes = settings.metrics_retention;
    }

    if (settings.task_activity_window !== undefined) {
      backendSettings.task_activity_window_seconds = settings.task_activity_window;
    }

    if (settings.connections_window !== undefined) {
      backendSettings.connections_window_minutes = settings.connections_window;
    }

    if (settings.latency_window !== undefined) {
      backendSettings.latency_window_minutes = settings.latency_window;
    }

    if (settings.onboarding_channel_ttl_seconds !== undefined) {
      backendSettings.onboarding_channel_ttl_seconds = settings.onboarding_channel_ttl_seconds;
    }

    if (settings.refresh_interval_seconds !== undefined) {
      backendSettings.refresh_interval_seconds = settings.refresh_interval_seconds;
    }

    if (settings.log_timestamp_format !== undefined) {
      backendSettings.log_timestamp_format = settings.log_timestamp_format;
    }

    if (settings.bucket_seconds !== undefined) {
      backendSettings.bucket_seconds = settings.bucket_seconds;
    }

    if (settings.payments_min_display !== undefined) {
      backendSettings.payments_min_display = settings.payments_min_display;
    }

    if (settings.feature_flags !== undefined) {
      backendSettings.feature_flags = settings.feature_flags;
    }

    await this.request('/settings', {
      method: 'POST',
      body: JSON.stringify(backendSettings),
    });
  }

  async resetSettings(): Promise<void> {
    await this.request('/settings', {
      method: 'DELETE',
    });
  }

  // Per-User Settings Management

  /**
   * Get effective settings for the current user (user overrides merged with system defaults).
   * Falls back to system settings if user settings endpoint is not available.
   */
  async getUserSettings(): Promise<Settings> {
    try {
      const backendSettings = await this.request<any>('/user-settings');
      return {
        badge_threshold: backendSettings.badge_threshold_minutes || 5,
        metrics_retention: backendSettings.metrics_retention_minutes || 360,
        task_activity_window: backendSettings.task_activity_window_seconds || 60,
        connections_window: backendSettings.connections_window_minutes || 5,
        latency_window: backendSettings.latency_window_minutes || 5,
        onboarding_channel_ttl_seconds: backendSettings.onboarding_channel_ttl_seconds || 30,
        refresh_interval_seconds: backendSettings.refresh_interval_seconds || 5,
        log_timestamp_format: backendSettings.log_timestamp_format || 'local',
        bucket_seconds: backendSettings.bucket_seconds || 30,
        payments_min_display: backendSettings.payments_min_display || 10,
        feature_flags: backendSettings.feature_flags || {},
      };
    } catch {
      // Fall back to system settings if user settings endpoint is unavailable
      return this.getSettings();
    }
  }

  /** Get only the user's personal overrides (without system defaults). */
  async getUserSettingsOverrides(): Promise<UserSettingsOverrides> {
    return this.request<UserSettingsOverrides>('/user-settings/overrides');
  }

  /** Update the current user's personal display preferences. */
  async updateUserSettings(settings: Partial<UserSettingsOverrides>): Promise<void> {
    await this.request('/user-settings', {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  }

  /** Reset user settings to system defaults. */
  async resetUserSettings(): Promise<void> {
    await this.request('/user-settings', {
      method: 'DELETE',
    });
  }

  // Metrics Configuration Management
  async getMetricsConfig(): Promise<any> {
    return this.request('/metrics/config');
  }

  async updateMetricsConfig(config: any): Promise<{ success: boolean; message: string }> {
    return this.request('/metrics/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async testOtlpConnection(
    endpoint: string
  ): Promise<{ success: boolean; message: string; latency_ms?: number }> {
    return this.request('/metrics/test-connection', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    });
  }

  // Channel Management
  async createChannel(channelConfig: any): Promise<{ config_id: string; channel?: any }> {
    // Generate a unique config_id for the new channel
    const configId = crypto.randomUUID();
    const channelWithId = { ...channelConfig, config_id: configId };

    const response = await this.request<{
      success: boolean;
      message: string;
      channel?: any;
    }>(`/channels/${encodeURIComponent(configId)}`, {
      method: 'PUT',
      body: JSON.stringify(channelWithId),
    });

    return { config_id: configId, channel: response.channel };
  }

  async updateChannel(
    configId: string,
    channelConfig: Partial<Channel>
  ): Promise<{ success: boolean; message: string; channel?: Channel }> {
    return this.request(`/channels/${encodeURIComponent(configId)}`, {
      method: 'PUT',
      body: JSON.stringify(channelConfig),
    });
  }

  async deleteChannel(configId: string): Promise<void> {
    await this.request(`/channels/${encodeURIComponent(configId)}`, {
      method: 'DELETE',
    });
  }

  async reloadChannel(configId: string): Promise<void> {
    await this.request(`/config/reload/${encodeURIComponent(configId)}`, {
      method: 'POST',
    });
  }

  async reloadAllChannels(): Promise<void> {
    await this.request('/config/reload', {
      method: 'POST',
    });
  }

  // Trust Registries
  async listTrustRegistries(): Promise<TrustRegistry[]> {
    return this.request<TrustRegistry[]>('/trust-registries');
  }

  async reconnectTrustRegistry(id: string): Promise<void> {
    await this.request<void>(`/trust-registries/${id}/reconnect`, {
      method: 'POST',
    });
  }

  // Onboarding Management
  async createTempOnboardChannel(protocol: string): Promise<{
    config_id: string;
    channel_name: string;
    endpoint_url: string;
    message: string;
    ttl_seconds: number;
  }> {
    return this.request('/onboard/create-temp-channel', {
      method: 'POST',
      body: JSON.stringify({ protocol }),
    });
  }

  async deleteTempOnboardChannel(configId: string): Promise<void> {
    await this.request(`/onboard/delete-temp-channel/${encodeURIComponent(configId)}`, {
      method: 'DELETE',
    });
  }

  // Metrics Management
  async truncateMetrics(): Promise<TruncateMetricsResult> {
    return this.request<TruncateMetricsResult>('/metrics/truncate', {
      method: 'POST',
    });
  }

  // Tasks (if available - placeholder for future implementation)
  async getTasks(): Promise<Task[]> {
    // This might need to be implemented in the backend
    return this.request<Task[]>('/tasks');
  }

  // Logs (if available - placeholder for future implementation)
  async getLogs(limit?: number): Promise<LogEntry[]> {
    const params = limit ? `?limit=${limit}` : '';
    return this.request<LogEntry[]>(`/logs${params}`);
  }

  // Truncate old / rotated log files
  async truncateOldLogs(): Promise<TruncateLogsResult> {
    return this.request<TruncateLogsResult>('/logs/truncate', {
      method: 'POST',
    });
  }

  // Download all logs as a ZIP archive (streaming for large files)
  async downloadLogs(): Promise<Blob> {
    const sessionToken = sessionStorage.getItem('session_token');
    const separator = '?';
    const url = sessionToken
      ? `${API_BASE}/logs/download${separator}session_token=${sessionToken}`
      : `${API_BASE}/logs/download`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...sessionManager.getAuthHeaders(),
      },
    });

    if (!response.ok) {
      let errorMessage = `Download failed: ${response.status} ${response.statusText}`;
      try {
        const errorBody = await response.json();
        if (errorBody.error) errorMessage = errorBody.error;
      } catch {
        // ignore parse error
      }
      throw new Error(errorMessage);
    }

    return response.blob();
  }

  // Permissions
  async getPermissions(): Promise<Record<string, boolean>> {
    return this.request<Record<string, boolean>>('/permissions');
  }

  // API Keys Management
  async listApiKeys(agentId: string): Promise<ApiKeyMeta[]> {
    return this.request<ApiKeyMeta[]>(`/api-keys/${encodeURIComponent(agentId)}`);
  }

  async createApiKey(
    agentId: string,
    clientId: string,
    labels?: Record<string, string>
  ): Promise<ApiKeyCreated> {
    return this.request<ApiKeyCreated>(`/api-keys/${encodeURIComponent(agentId)}`, {
      method: 'POST',
      body: JSON.stringify({ client_id: clientId, labels }),
    });
  }

  async getApiKey(agentId: string, keyId: string): Promise<ApiKeyMeta> {
    return this.request<ApiKeyMeta>(
      `/api-keys/${encodeURIComponent(agentId)}/${encodeURIComponent(keyId)}`
    );
  }

  async revokeApiKey(agentId: string, keyId: string, actor?: string): Promise<void> {
    await this.request(
      `/api-keys/${encodeURIComponent(agentId)}/${encodeURIComponent(keyId)}/revoke`,
      {
        method: 'POST',
        body: JSON.stringify({ actor: actor || 'dashboard' }),
      }
    );
  }

  async rotateApiKey(agentId: string, keyId: string, actor?: string): Promise<ApiKeyCreated> {
    return this.request<ApiKeyCreated>(
      `/api-keys/${encodeURIComponent(agentId)}/${encodeURIComponent(keyId)}/rotate`,
      {
        method: 'POST',
        body: JSON.stringify({ actor: actor || 'dashboard' }),
      }
    );
  }

  async deleteApiKey(agentId: string, keyId: string): Promise<void> {
    await this.request(`/api-keys/${encodeURIComponent(agentId)}/${encodeURIComponent(keyId)}`, {
      method: 'DELETE',
    });
  }

  // Outbound Credentials Management
  async listCredentials(agentId: string): Promise<OutboundCredentialMeta[]> {
    return this.request<OutboundCredentialMeta[]>(`/credentials/${encodeURIComponent(agentId)}`);
  }

  async createCredential(
    agentId: string,
    targetIdentifier: string,
    credentialType: CredentialType,
    credentialValue: string,
    headerName?: string,
    headerFormat?: string,
    labels?: Record<string, string>
  ): Promise<OutboundCredentialMeta> {
    return this.request<OutboundCredentialMeta>(`/credentials/${encodeURIComponent(agentId)}`, {
      method: 'POST',
      body: JSON.stringify({
        target_identifier: targetIdentifier,
        credential_type: credentialType,
        credential_value: credentialValue,
        header_name: headerName,
        header_format: headerFormat,
        labels,
        actor: 'dashboard',
      }),
    });
  }

  async getCredential(agentId: string, credentialId: string): Promise<OutboundCredentialMeta> {
    return this.request<OutboundCredentialMeta>(
      `/credentials/${encodeURIComponent(agentId)}/${encodeURIComponent(credentialId)}`
    );
  }

  async updateCredential(
    agentId: string,
    credentialId: string,
    credentialValue: string,
    targetIdentifier?: string,
    labels?: Record<string, string>
  ): Promise<OutboundCredentialMeta> {
    return this.request<OutboundCredentialMeta>(
      `/credentials/${encodeURIComponent(agentId)}/${encodeURIComponent(credentialId)}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          credential_value: credentialValue,
          target_identifier: targetIdentifier,
          labels,
          actor: 'dashboard',
        }),
      }
    );
  }

  async deleteCredential(agentId: string, credentialId: string): Promise<void> {
    await this.request(
      `/credentials/${encodeURIComponent(agentId)}/${encodeURIComponent(credentialId)}`,
      {
        method: 'DELETE',
      }
    );
  }

  // DID:webvh Identity Management
  async listDidWebVhIdentities(): Promise<any> {
    const response = await this.get<any>('/identities');
    return response.data;
  }

  async getDidWebVhIdentity(id: string): Promise<any> {
    const response = await this.get<any>(`/identities/${encodeURIComponent(id)}`);
    return response.data;
  }

  async createDidWebVhIdentity(request: any): Promise<any> {
    const response = await this.post<any>('/identities', request);
    return response.data;
  }

  async updateDidWebVhIdentity(id: string, didDocument: any, updateKeys?: string[]): Promise<any> {
    const response = await this.post<any>(`/identities/${encodeURIComponent(id)}/update`, {
      did_document: didDocument,
      update_keys: updateKeys,
    });
    return response.data;
  }

  async deleteDidWebVhIdentity(id: string): Promise<void> {
    await this.delete(`/identities/${encodeURIComponent(id)}`);
  }

  async getIdentityTrustScore(id: string): Promise<any> {
    const response = await this.get<any>(`/identities/${encodeURIComponent(id)}/trust-score`);
    return response.data;
  }

  async getIdentityVersionHistory(id: string): Promise<any> {
    const response = await this.get<any>(`/identities/${encodeURIComponent(id)}/history`);
    return response.data;
  }

  async rotateIdentityKeys(id: string, params?: { pre_rotation_seconds?: number }): Promise<any> {
    const response = await this.post<any>(
      `/identities/${encodeURIComponent(id)}/rotate-keys`,
      params
    );
    return response.data;
  }

  async transferIdentityOwnership(id: string, request: any): Promise<any> {
    const response = await this.post<any>(
      `/identities/${encodeURIComponent(id)}/transfer`,
      request
    );
    return response.data;
  }

  async resolveDidWebVh(did: string, versionId?: string, versionTime?: string): Promise<any> {
    const params = new URLSearchParams({ did });
    if (versionId) params.append('versionId', versionId);
    if (versionTime) params.append('versionTime', versionTime);
    const response = await this.get<any>(`/resolve?${params.toString()}`);
    return response.data;
  }

  async verifyDidWebVh(did: string, dna?: any): Promise<any> {
    const response = await this.post<any>(
      `/verify/${encodeURIComponent(did)}`,
      dna ? { dna } : undefined
    );
    return response.data;
  }

  // Debug: Export storage with PII redaction, encrypted with Ed25519 public key
  async exportStorage(publicKeyPem: string): Promise<Blob> {
    const response = await fetch(`${API_BASE}/debug/export-storage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...sessionManager.getAuthHeaders(),
      },
      body: JSON.stringify({ public_key_pem: publicKeyPem }),
    });

    if (!response.ok) {
      let errorMessage = `Export failed: ${response.status} ${response.statusText}`;
      try {
        const errorBody = await response.text();
        if (errorBody) errorMessage += ` - ${errorBody}`;
      } catch (_) {}
      throw new Error(errorMessage);
    }

    return response.blob();
  }

  // Admin: Backup storage (full, with PIIs) — downloads backup.tgwbak
  async backupStorage(): Promise<Blob> {
    const response = await fetch(`${API_BASE}/admin/backup-storage`, {
      method: 'POST',
      headers: {
        ...sessionManager.getAuthHeaders(),
      },
    });

    if (!response.ok) {
      let errorMessage = `Backup failed: ${response.status} ${response.statusText}`;
      try {
        const errorBody = await response.text();
        if (errorBody) errorMessage += ` - ${errorBody}`;
      } catch (_) {}
      throw new Error(errorMessage);
    }

    return response.blob();
  }

  // Admin: Restore storage from backup.tgwbak upload
  async restoreStorage(file: File): Promise<{ status: string; message: string }> {
    const formData = new FormData();
    formData.append('backup', file);

    const response = await fetch(`${API_BASE}/admin/restore-storage`, {
      method: 'POST',
      headers: {
        ...sessionManager.getAuthHeaders(),
      },
      body: formData,
    });

    if (!response.ok) {
      let errorMessage = `Restore failed: ${response.status} ${response.statusText}`;
      try {
        const errorBody = await response.text();
        if (errorBody) errorMessage += ` - ${errorBody}`;
      } catch (_) {}
      throw new Error(errorMessage);
    }

    return response.json();
  }
}

// API Key types
export interface ApiKeyMeta {
  key_id: string;
  agent_id: string;
  client_id: string;
  prefix: string;
  status: 'active' | 'revoked';
  labels: Record<string, string>;
  created_at: string;
  created_by: string;
  revoked_at?: string;
  revoked_by?: string;
  last_used_at?: string;
  use_count: number;
}

export interface ApiKeyCreated {
  key_id: string;
  agent_id: string;
  client_id: string;
  secret: string;
  created_at: string;
  rotated_from?: string;
}

// Outbound Credential types
export type CredentialType = 'api_key' | 'bearer_token' | 'basic_auth' | 'custom';

export interface OutboundCredentialMeta {
  credential_id: string;
  agent_id: string;
  target_identifier: string;
  credential_type: CredentialType;
  header_name: string;
  header_format: string;
  labels: Record<string, string>;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export const apiClient = new APIClient();
